import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

interface Props {
  page: string;
  children: ReactNode;
}
interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Page-level error boundary — isolates crashes to individual pages.
 * If one page (e.g. TradeQueue) crashes, other pages still work.
 */
export default class PageErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[MCT] Page "${this.props.page}" crashed:`, error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      // 2026-04-28: detect chunk-load failures (deploy mid-navigation,
      // server briefly down, stale bundle hashes). Offer a HARD reload —
      // a state reset alone doesn't help because React.lazy caches the
      // failed import promise and will keep returning the rejection.
      const msg = this.state.error?.message ?? "";
      const isChunkLoadError =
        msg.includes("dynamically imported module") ||
        msg.includes("Failed to fetch") ||
        msg.includes("Loading chunk") ||
        msg.includes("ChunkLoadError");
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 text-center p-8">
          <AlertTriangle size={32} className="text-red-400" />
          <div>
            <h2 className="text-sm font-bold text-red-400 mb-1">
              {isChunkLoadError ? "App update available" : `${this.props.page} page crashed`}
            </h2>
            <p className="text-xs text-zinc-500 font-mono bg-zinc-900 rounded px-3 py-2 mb-4 max-w-md break-all">
              {isChunkLoadError
                ? "A newer version of the app was deployed. Reload to fetch the latest."
                : (msg || "Unknown error")}
            </p>
            <button
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs rounded transition-colors"
              onClick={() => {
                if (isChunkLoadError) {
                  // Clear the lazy-retry session flag so a future failure can
                  // attempt the auto-reload again, then hard-reload now.
                  try { sessionStorage.removeItem("lazy_chunk_reload_attempted"); } catch { /* ignore */ }
                  window.location.reload();
                } else {
                  this.setState({ hasError: false, error: null });
                }
              }}
            >
              {isChunkLoadError ? "Reload now" : "Reload page"}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
